/**
 * Obtém as chaves (nomes) associadas aos valores específicos de um enum.
 *
 * @template T - O tipo do enum.
 * @template U - O tipo dos valores do enum.
 *
 * @param {Array<U>} enumValues - Um array contendo os valores do enum.
 * @param {T} enumType - O enum para o qual deseja obter as chaves.
 *
 * @returns {Array<keyof T>} - Um array contendo as chaves (nomes) associadas aos valores fornecidos.
 *
 * @throws {Error} Será lançado um erro se o valor do enum não for encontrado.
 *
 * @example
 * const enumValues = [StatusPedido.FATURADO, StatusPedido.ABERTO];
 * // ou const enumValues = ['Faturado', 'Aberto'];
 * const enumType = StatusPedido;
 * const keys = getKeysOfEnum(enumValues, enumType);
 * console.log(keys); // ['FATURADO', 'ABERTO']
 */
export const getKeysOfEnum = <T extends Record<string, U>, U>(
  enumValues: U[],
  enumType: T
): Array<keyof T> => {
  return enumValues.map((value) => {
    const find = Object.keys(enumType).find((key) => enumType[key] === value);
    if (!find) {
      throw new Error("Valor do enum não encontrado");
    }
    return find as keyof T;
  });
};

export const getOneKeyOfEnum = <T extends Record<string, U>, U>(
  enumValue: U,
  enumType: T
): keyof T => {
  return getKeysOfEnum([enumValue], enumType)[0];
};

/**
 * Retorna os valores do enum como um Array
 *
 * @template T
 * @param {T} enumObj - O enum.
 * @returns {T[keyof T][]} - Um novo array com os valores do enum.
 *
 * @example
 * console.log(getEnumValues(FichaConsumoMaterialCaracteristicaMaterial));
 * // [ 'EXCLUSIVO DE CUSTO', 'PARTE DE CONJUNTO', 'ITEM PRINCIPAL', 'EXCLUSIVO DE MOSTRUÁRIO' ];
 */
export const getEnumValues = <T>(enumObj: T): T[keyof T][] => {
  return Object.values(enumObj as any) as T[keyof T][];
};

/**
 * Tipo que representa o formato de retorno da função `getEnumObjNameDescritivo`.
 * O retorno é um array de objetos onde as chaves são baseadas nos valores de `OptionLabel` e `OptionValue`.
 *
 * @template OptionLabel - O nome da chave que representa a label.
 * @template OptionValue - O nome da chave que representa o valor.
 */
type EnumObjNameDescritivoReturn<
  OptionLabel extends string,
  OptionValue extends string
> = ({
  [K in OptionValue]: EnumValue;
} & { [L in OptionLabel]: EnumDescritivoValue })[];

/**
 * Tipo que representa um enum como um objeto.
 */
type EnumType = Record<string, string | number>;
type EnnumKey = keyof EnumType;
type EnumKeyOrValue = EnumType | EnnumKey | EnumValue;

/**
 * Tipo que representa os valores de um enum.
 */
type EnumValue = EnumType[keyof EnumType];

/**
 * Tipo que representa um enumDescritivo, onde a key é o valor de um enum não descritivo.
 */
type EnumDescritivoType = Record<EnumValue, string | number>;

/**
 * Tipo que representa os valores de um enumDescritivo.
 */
type EnumDescritivoValue = EnumDescritivoType[EnumValue];

/**
 * Gera um array de objetos que representam os valores de um enum e suas descrições.
 *
 * @template OptionValue - O nome da chave que representa o value (padrão é "name").
 * @template OptionLabel - O nome da chave que representa a label (padrão é "label").
 * @param {EnumType} enumValue - O enum.
 * @param {EnumDescritivoType} enumDescritivo - O enumDescritivo.
 * @param {{ optionLabel?: OptionLabel; optionValue?: OptionValue }} [opt] - Opções para definir os nomes das chaves no retorno.
 * @returns {EnumObjNameDescritivoReturn<OptionLabel, OptionValue>} Um array de objetos com as chaves definidas por `optionLabel` e `optionValue`.
 *
 * @example
 * const Colors = {
 *   RED: "RED",
 *   BLUE: "BLUE",
 *   GREEN: "GREEN",
 * };
 *
 * const colorDescriptions = {
 *   RED: "Color Red",
 *   BLUE: "Color Blue",
 *   GREEN: "Color Green",
 * };
 *
 * const result = getEnumObjNameDescritivo(Colors, colorDescriptions, {
 *   optionLabel: "description",
 *   optionValue: "color",
 * });
 * // Retorno: [{ color: "RED", description: "Color Red" }, ...]
 */
export const getEnumObjNameDescritivo = <
  OptionValue extends string = "name",
  OptionLabel extends string = "label"
>(
  enumValue: EnumType,
  enumDescritivo: EnumDescritivoType,
  opt?: { optionLabel?: OptionLabel; optionValue?: OptionValue }
): EnumObjNameDescritivoReturn<OptionLabel, OptionValue> => {
  return getEnumValues(enumValue).map((s) => ({
    [opt?.optionValue ?? "name"]: s,
    [opt?.optionLabel ?? "label"]: enumDescritivo[s],
  })) as EnumObjNameDescritivoReturn<OptionLabel, OptionValue>;
};

export const getEnumValue = (type: EnumKeyOrValue, enumType: EnumType): any => {
  return (
    enumType[type as EnnumKey] ??
    enumType[getOneKeyOfEnum(type as EnumValue, enumType)] ??
    type
  );
};

export const enumEquals = (
  type1: EnumKeyOrValue,
  type2: EnumKeyOrValue,
  enumType: EnumType
): any => {
  return getEnumValue(type1, enumType) === getEnumValue(type2, enumType);
};
